<template>
	<BaseDialog
		v-model:toggle="dialog"
		:fullscreen="fullscreen"
		:force-fullscreen="fullscreen"
		close-on-escape
		:handle-prev-route="handlePrevRoute"
		@dialogClose="close"
	>
		<template v-if="$slots.activator" #activator="activatorProps">
			<slot name="activator" v-bind="activatorProps"></slot>
		</template>
		<template #default="{ isActive }">
			<!--
				PDF und Bild zeigt derselbe Betrachter wie das Prüf-Cockpit: Zoom,
				Drehen, Vollbild, Download und Drucken an einer Stelle, statt zwei
				verschiedener Vorschauen je nach Dateityp.
			-->
			<v-card
				v-if="id && !containerId && file"
				:class="['preview', { 'preview--windowed': !fullscreen }]"
				data-test="document-preview"
				flat
				rounded="0"
			>
				<div class="preview__head">
					<v-icon :color="kind === 'pdf' ? 'error' : undefined" size="16">
						{{
							kind === 'pdf'
								? 'fa-regular fa-file-pdf'
								: 'fa-regular fa-file-image'
						}}
					</v-icon>
					<span class="preview__name" :title="fileName">{{ fileName }}</span>
					<ButtonIcon
						:tooltip="{ text: 'common.close.name' }"
						data-test="document-preview-close"
						icon="fal fa-times"
						notext
						variant="text"
						@click="close"
					></ButtonIcon>
				</div>
				<div class="preview__body">
					<ReceiptViewer
						v-if="kind"
						:key="id"
						:document-id="id"
						:file-name="fileName"
						:kind="kind"
					></ReceiptViewer>
				</div>
			</v-card>

			<DocumentContainerView
				v-else-if="!id && containerId"
				:id="containerId"
				@close="isActive.value = false"
			></DocumentContainerView>
		</template>
	</BaseDialog>
</template>

<script setup lang="ts">
import BaseDialog from '@/components/templates/BaseDialog.vue'
import ButtonIcon from '@/components/templates/Base/ButtonIcon.vue'
import ReceiptViewer from '@/components/modules/ReviewDocuments/Cockpit/ReceiptViewer.vue'
import { useDocumentStore } from '@/stores/modules/document'
import { computed, onBeforeMount, ref } from 'vue'
import DocumentContainerView from './DocumentContainerView.vue'

const { id, fullscreen, containerId } = defineProps({
	id: {
		type: Number,
		default: undefined,
	},
	containerId: {
		type: Number,
		default: undefined,
	},
	fullscreen: {
		default: true,
		required: false,
		type: Boolean,
	},
	/** Nur noch für Bestandsaufrufe; der Betrachter füllt die Dialoghöhe. */
	heightFactor: {
		default: 1,
		required: false,
		type: Number,
	},
	handlePrevRoute: {
		default: true,
		required: false,
		type: Boolean,
	},
})
const emit = defineEmits(['close'])

const { show, documentName, showWhat } = useDocumentStore()

const file = ref<Api.Document>()

onBeforeMount(async () => {
	if (id && !containerId) {
		file.value = (await show({ id: id })) as Api.Document
	}
})

/** 'pdf' | 'img' — andere Typen kann der Browser nicht zeigen. */
const kind = computed<'pdf' | 'img' | undefined>(() => {
	if (!file.value) return undefined
	const what = showWhat(file.value)
	return what === 'pdf' || what === 'img' ? what : undefined
})

const fileName = computed(() =>
	file.value ? documentName({ id: file.value.id, file: file.value }) : ''
)

const dialog = defineModel('dialog', { default: true })

const close = () => {
	emit('close')
	dialog.value = false
}
</script>

<style scoped>
.preview {
	display: flex;
	flex-direction: column;
	height: 100%;
}

/* Ohne Vollbild: fast bildschirmhoch, damit der Beleg lesbar bleibt. */
.preview--windowed {
	height: 90vh;
}

.preview__head {
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 44px;
	padding: 0 4px 0 16px;
	border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.preview__name {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 0.9375rem;
	font-weight: 500;
}

.preview__body {
	display: flex;
	flex: 1 1 auto;
	min-height: 0;
	padding: 8px;
}
</style>
